import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  FormControl,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import {
  useState,
  SyntheticEvent,
  useCallback,
  FormEvent,
  useMemo,
} from "react";
import { useCreateLicensesMutation } from "src/api/services/providersApi";
import { reversedStatesMap, statesMap } from "src/constants/usStates";
import { desktopSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "src/components/Buttons/LoadingButton";

interface Props {
  open: boolean;
  onClose: () => void;
  providerId: number;
  licenses: string[];
}

const CreateProviderLicensesModal = ({
  open,
  onClose,
  providerId,
  licenses,
}: Props) => {
  const [selectedStates, setSelectedStates] = useState<string[]>(licenses);
  const [createLicenses, { isLoading }] = useCreateLicensesMutation();

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    value: string[]
  ) => {
    setSelectedStates(value);
  };

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      createLicenses({
        provider_id: providerId,
        body: {
          state_codes: selectedStates.map((state) => reversedStatesMap[state]),
        },
      })
        .unwrap()
        .then(onClose);
    },
    [createLicenses, onClose, providerId, selectedStates]
  );

  const states = useMemo(() => {
    return Object.values(statesMap);
  }, []);

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Licenses
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        <FormControl fullWidth>
          <Autocomplete
            onChange={handleChange}
            multiple
            value={selectedStates}
            fullWidth
            disablePortal
            options={states}
            renderInput={(params) => <TextField {...params} label="States" />}
          />
        </FormControl>

        <Stack direction="row" gap={2} sx={{ marginTop: "auto" }}>
          <Button fullWidth variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton fullWidth loading={isLoading} type="submit">
            Create
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreateProviderLicensesModal;
